import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode, useMemo } from 'react';
import { getCanonicalUserKey, debugUserKey } from '../utils/id-utils';

/**
 * V150.34: Multi-Tier Photo Caching System
 * L1 Cache: React State (In-Memory) - INSTANT (0-5ms)
 * L2 Cache: Browser HTTP Cache - FAST (5-50ms)
 * L3 Source: Cloudinary CDN - RELIABLE (100-300ms first load)
 *
 * CRITICAL: Database stores Cloudinary URLs only (NOT blob URLs!)
 * Blob URLs expire after session and cannot be persisted.
 */

interface PhotoCache {
  [userId: string]: {
    url: string;
    timestamp: number;
    source: 'cache' | 'cloudinary';
  };
}

interface PhotoContextType {
  // V150.34: L1 Cache Management (React State) + V151.17: Canonical keys
  getCachedPhotoUrl: (userId: string | number) => string | null;
  updateUserPhoto: (userId: string | number, url: string, source?: 'cache' | 'cloudinary') => void;
  clearCache: (userId?: string | number) => void;
  getCacheStats: () => { size: number; users: string[] };

  // Loading state management
  isPhotoLoading: (userId: string) => boolean;
  setPhotoLoading: (userId: string, loading: boolean) => void;

  // Real-time updates
  refreshPhoto: (userId: string) => Promise<void>;
}

interface PhotoProviderProps {
  children: ReactNode;
}

// Create context with default empty implementation
const defaultContext: PhotoContextType = {
  getCachedPhotoUrl: () => null,
  updateUserPhoto: () => {},
  clearCache: () => {},
  getCacheStats: () => ({ size: 0, users: [] }),
  isPhotoLoading: () => false,
  setPhotoLoading: () => {},
  refreshPhoto: async () => {}
};

const PhotoContext = createContext<PhotoContextType>(defaultContext);

export const PhotoContextProvider = ({ children }: PhotoProviderProps) => {
  // V145.4: NUCLEAR CACHE PERSISTENCE - Initialize with localStorage restoration
  const [photoCache, setPhotoCache] = useState<PhotoCache>(() => {
    try {
      const persistedCache = localStorage.getItem('blissnet_photo_cache_v145');
      if (persistedCache) {
        const parsed = JSON.parse(persistedCache);
        console.log('🔄 V145.4: Restored photo cache from localStorage:', Object.keys(parsed).length, 'entries');

        // Validate cache entries and filter out expired ones (older than 24 hours)
        const now = Date.now();
        const validCache: PhotoCache = {};
        let restoredCount = 0;

        for (const [key, entry] of Object.entries(parsed)) {
          const typedEntry = entry as any;
          if (typedEntry && typeof typedEntry === 'object' && typedEntry.url && typedEntry.timestamp) {
            const age = now - typedEntry.timestamp;
            if (age < 24 * 60 * 60 * 1000) { // 24 hours
              validCache[key] = typedEntry;
              restoredCount++;
            }
          }
        }

        console.log('✅ V145.4: Restored', restoredCount, 'valid cache entries (filtered expired)');
        return validCache;
      }
    } catch (error) {
      console.warn('⚠️ V145.4: Failed to restore cache from localStorage:', error);
    }

    console.log('🆕 V145.4: Starting with empty photo cache');
    return {};
  });

  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({});

  // V150.34: Get cached photo (L1 cache check) + V151.17: Canonical keys
  const getCachedPhotoUrl = useCallback((userId: string | number): string | null => {
    const canonicalKey = debugUserKey(userId, 'PhotoContext L1 Cache');
    const cached = photoCache[canonicalKey];

    if (cached) {
      const age = Date.now() - cached.timestamp;
      const DEBUG_CACHE = localStorage.getItem('DEBUG_CACHE') === '1';
      if (DEBUG_CACHE) {
        console.log('✅ V151.17: L1 Cache HIT -', canonicalKey.slice(0, 8), '-', Math.round(age / 1000), 's old');
      }
      return cached.url;
    }

    const DEBUG_CACHE = localStorage.getItem('DEBUG_CACHE') === '1';
    if (DEBUG_CACHE) {
      console.log('❌ V151.17: L1 Cache MISS -', canonicalKey.slice(0, 8));
    }
    return null;
  }, [photoCache]);

  // V150.34: Update cache (store in L1) + V151.17: Canonical keys + V145.4: Persistent cache
  const updateUserPhoto = useCallback((
    userId: string | number,
    url: string,
    source: 'cache' | 'cloudinary' = 'cloudinary'
  ) => {
    // V151.3: STRICT blob URL blocking - production level
    if (url.startsWith('blob:')) {
      console.log('🚫 V151.3: Blob URL blocked from cache (display-only)');
      return;
    }
    // V320: Never cache Google placeholder – only Cloudinary (or allowed CDN) as final
    if (url.includes('googleusercontent.com') || url.includes('lh3.googleusercontent')) {
      console.log('🚫 V320: Google placeholder blocked from cache (must go through Cloudinary pipeline)');
      return;
    }

    const canonicalKey = debugUserKey(userId, 'PhotoContext L1 Cache UPDATE');
    const DEBUG_CACHE = localStorage.getItem('DEBUG_CACHE') === '1';
    if (DEBUG_CACHE) {
      console.log('💾 V151.17: L1 Cache UPDATE -', canonicalKey.slice(0, 8));
      console.log('   Source:', source);
      console.log('   URL:', url.slice(0, 60) + '...');
    }

    const newCacheEntry = {
      url,
      timestamp: Date.now(),
      source
    };

    setPhotoCache(prev => {
      const newCache = {
        ...prev,
        [canonicalKey]: newCacheEntry
      };

      // V145.4: NUCLEAR CACHE PERSISTENCE - Save to localStorage for page reload survival
      try {
        localStorage.setItem('blissnet_photo_cache_v145', JSON.stringify(newCache));
        console.log('💾 V145.4: Cache persisted to localStorage for reload survival');
      } catch (error) {
        console.warn('⚠️ V145.4: Failed to persist cache to localStorage:', error);
      }

      return newCache;
    });

    // Clear loading state
    setLoadingStates(prev => ({
      ...prev,
      [canonicalKey]: false
    }));

    // V150.34: Dispatch event for other components + V151.17: Canonical key
    window.dispatchEvent(new CustomEvent('photoUpdated', {
      detail: { userId: canonicalKey, photoUrl: url }
    }));
  }, []);

  // V150.34: Clear cache (for logout, etc.) + V151.17: Canonical keys + V145.4: Persistent cache
  const clearCache = useCallback((userId?: string | number) => {
    if (userId) {
      const canonicalKey = debugUserKey(userId, 'PhotoContext L1 Cache CLEAR');
      console.log('🧹 V151.17: Clearing L1 cache for user:', canonicalKey.slice(0, 8));
      setPhotoCache(prev => {
        const newCache = { ...prev };
        delete newCache[canonicalKey];

        // V145.4: Update persistent cache
        try {
          localStorage.setItem('blissnet_photo_cache_v145', JSON.stringify(newCache));
        } catch (error) {
          console.warn('⚠️ V145.4: Failed to update persistent cache:', error);
        }

        return newCache;
      });
    } else {
      console.log('🧹 V151.17: Clearing ALL L1 cache');
      setPhotoCache({});

      // V145.4: Clear persistent cache
      try {
        localStorage.removeItem('blissnet_photo_cache_v145');
        console.log('🧹 V145.4: Cleared persistent cache from localStorage');
      } catch (error) {
        console.warn('⚠️ V145.4: Failed to clear persistent cache:', error);
      }
    }
  }, []);

  // V150.34: Cache statistics
  const getCacheStats = useCallback(() => {
    const size = Object.keys(photoCache).length;
    const users = Object.keys(photoCache);

    console.log('📊 V150.34: L1 Cache Stats:', {
      cachedUsers: size,
      totalMemory: JSON.stringify(photoCache).length + ' bytes'
    });

    return { size, users };
  }, [photoCache]);

  // Loading state management
  const isPhotoLoading = useCallback((userId: string): boolean => {
    return loadingStates[userId] || false;
  }, [loadingStates]);

  const setPhotoLoading = useCallback((userId: string, loading: boolean) => {
    setLoadingStates(prev => ({
      ...prev,
      [userId]: loading
    }));
  }, []);

  // V150.34: Refresh photo from database/Cloudinary
  const refreshPhoto = useCallback(async (userId: string) => {
    console.log('🔄 V150.34: Refreshing photo for user:', userId.slice(0, 8));
    setPhotoLoading(userId, true);

    try {
      // V163.5: Use NetworkDataContext for user data instead of direct query
      console.warn('⚠️ V163.5: PhotoContext fetchFromDatabase() - Should use NetworkDataContext for user data');

      // For now, try to get from NetworkDataContext if available
      // Otherwise skip the query to prevent duplicates
      const { useUser, useCurrentUser } = await import('./NetworkDataContext');

      // This is a fallback - ideally components should use NetworkDataContext directly
      console.log('⚠️ V163.5: PhotoContext skipping direct user query - use NetworkDataContext');
      return;
    } catch (error) {
      console.error('❌ V150.34: Refresh failed:', error);
    } finally {
      setPhotoLoading(userId, false);
    }
  }, [updateUserPhoto, clearCache, setPhotoLoading]);

  // V145.4: Listen for onboarding completion to ensure cache persistence
  useEffect(() => {
    const handleOnboardingComplete = (event: CustomEvent) => {
      console.log('🎯 V145.4: Onboarding completion detected - ensuring cache persistence');

      try {
        // Force save current cache to localStorage
        localStorage.setItem('blissnet_photo_cache_v145', JSON.stringify(photoCache));
        console.log('✅ V145.4: Photo cache forcibly saved before page reload');
      } catch (error) {
        console.error('❌ V145.4: Failed to save cache before page reload:', error);
      }
    };

    window.addEventListener('onboardingComplete', handleOnboardingComplete as EventListener);

    return () => {
      window.removeEventListener('onboardingComplete', handleOnboardingComplete as EventListener);
      console.log('🧹 V150.34: Component unmounting - clearing L1 cache');
    };
  }, [photoCache]);

  // 🚨 NUCLEAR STABILIZATION: Memoize context value with stable dependencies
  const contextValue = useMemo(() => ({
    getCachedPhotoUrl,
    updateUserPhoto,
    clearCache,
    getCacheStats,
    isPhotoLoading,
    setPhotoLoading,
    refreshPhoto
  }), [getCachedPhotoUrl, updateUserPhoto, clearCache, getCacheStats, isPhotoLoading, setPhotoLoading, refreshPhoto]); // V157.3 TASK 3: Use function dependencies for better stability

  // V157.3 TASK 3: Provider stability monitoring (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 V157.3: PhotoContext provider value changed');
    }
  }, [contextValue]);

  return (
    <PhotoContext.Provider value={contextValue}>
      {children}
    </PhotoContext.Provider>
  );
};

export const usePhotoContext = (): PhotoContextType => {
  const context = useContext(PhotoContext);
  if (!context) {
    // Return default implementation if not in provider
    return defaultContext;
  }
  return context as PhotoContextType;
};

// V150.34: Backward compatibility exports
export const PhotoProvider = PhotoContextProvider;
export { PhotoContextProvider as default };


